/** Standard page header used at the top of every dashboard route. */
import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

type Tint = 'blue' | 'coral' | 'green' | 'amber' | 'slate';

const TINT_CLASS: Record<Tint, string> = {
  blue: 'bg-[#4f6bff]/10 text-[#4f6bff]',
  coral: 'bg-[#ff7a59]/12 text-[#ff7a59]',
  green: 'bg-[#22c55e]/12 text-[#16a34a]',
  amber: 'bg-[#ffc542]/20 text-[#d39400]',
  slate: 'bg-[color:var(--kz-color-surface-sunken)] text-[color:var(--kz-color-ink-secondary)]',
};

export function PageHeader({
  title,
  description,
  actions,
  icon: Icon,
  tint = 'blue',
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
  icon?: LucideIcon;
  tint?: Tint;
}) {
  return (
    <header className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        {Icon && (
          <span className={cn('grid size-11 shrink-0 place-items-center rounded-2xl', TINT_CLASS[tint])}>
            <Icon className="size-5" />
          </span>
        )}
        <div className="min-w-0">
          <h1 className="text-2xl font-extrabold tracking-tight md:text-3xl">{title}</h1>
          {description && (
            <p className="mt-1 text-sm text-[color:var(--kz-color-ink-secondary)]">{description}</p>
          )}
        </div>
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </header>
  );
}
